import { Calendar, ArrowRight, Clock, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useBlogPosts, useBlogCategories } from '../hooks/useBlog';
import useSEO from '../hooks/useSEO';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('categoria') || 'tutti';

  const { categories } = useBlogCategories();
  const { posts, loading } = useBlogPosts(activeCategory);
  const activeCat = categories.find(c => c.slug === activeCategory);

  useSEO({
    title: activeCat
      ? `${activeCat.name} — Blog AeroDeals`
      : 'Blog — Guide, News e Approfondimenti sull\'Aviazione',
    description: activeCat
      ? `${activeCat.description}. Leggi articoli e guide sul blog di AeroDeals.`
      : 'Guide all\'acquisto e alla vendita di aeromobili, news di settore, mercato aeronautico, costi di gestione, confronti tra modelli. Blog ufficiale AeroDeals.',
    keywords: 'blog aviazione, guide acquisto aereo, vendere aereo, ultraleggeri, costi gestione aeromobile, mercato aeronautico',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sortedPosts = useMemo(() => {
    // Pinned first, then by date desc (server already sorts, but we ensure it after normalization)
    return [...posts].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.date) - new Date(a.date);
    });
  }, [posts]);

  const setCategory = (slug) => {
    if (slug === 'tutti') {
      setSearchParams({});
    } else {
      setSearchParams({ categoria: slug });
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="blog-page">
      {/* Hero */}
      <section className="py-16 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Blog</p>
          <h1 className="text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Approfondimenti & Guide
          </h1>
          <p className="text-base text-zinc-400 max-w-2xl">
            Analisi di mercato, guide pratiche, confronti e consigli per acquistare, vendere e gestire il tuo velivolo.
          </p>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section className="border-b border-zinc-800/50 sticky top-16 z-30 bg-[#09090B]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide" data-testid="blog-category-tabs">
            <button
              onClick={() => setCategory('tutti')}
              className={`whitespace-nowrap px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${activeCategory === 'tutti' ? 'text-[#C5A059] border-b-2 border-[#C5A059]' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid="blog-tab-tutti"
            >
              Tutti
            </button>
            {categories.map(cat => (
              <button
                key={cat.slug}
                onClick={() => setCategory(cat.slug)}
                className={`whitespace-nowrap px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${activeCategory === cat.slug ? 'text-[#C5A059] border-b-2 border-[#C5A059]' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid={`blog-tab-${cat.slug}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Category description when filtered */}
      {activeCategory !== 'tutti' && activeCat && (
        <section className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-sm text-zinc-500" data-testid="blog-category-description">
              {activeCat.description}
            </p>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-500 gap-2" data-testid="blog-loading">
              <Loader2 className="animate-spin" size={16} /> Caricamento articoli...
            </div>
          ) : sortedPosts.length === 0 ? (
            <p className="text-zinc-500 text-center py-16" data-testid="blog-empty">Nessun articolo trovato per questa categoria.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedPosts.map((post, i) => {
                const cat = categories.find(c => c.slug === post.category);
                const isFirst = i === 0 && activeCategory === 'tutti';
                return (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.slug}
                    className={`group border border-zinc-800 bg-zinc-900/30 overflow-hidden hover:border-zinc-600 transition-all ${isFirst ? 'md:col-span-2 lg:col-span-2 md:grid md:grid-cols-2' : ''}`}
                    data-testid={`blog-post-${post.slug}`}
                  >
                    <div className={`overflow-hidden ${isFirst ? 'aspect-[16/10] md:aspect-auto md:min-h-[320px]' : 'aspect-[16/10]'}`}>
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        {cat && <span className="text-[10px] font-mono uppercase tracking-wider text-[#C5A059]">{cat.name}</span>}
                        {post.pinned && <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500 border border-amber-500/30 px-2 py-0.5">Pinnato</span>}
                        <span className="text-xs text-zinc-600 flex items-center gap-1"><Calendar size={10} /> {new Date(post.date).toLocaleDateString('it-IT')}</span>
                        <span className="text-xs text-zinc-600 flex items-center gap-1"><Clock size={10} /> {post.readTime}</span>
                      </div>
                      <h2 className={`font-medium text-white mb-3 group-hover:text-[#C5A059] transition-colors line-clamp-2 ${isFirst ? 'text-xl sm:text-2xl' : 'text-base'}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {post.title}
                      </h2>
                      <p className="text-sm text-zinc-400 leading-relaxed mb-4 line-clamp-3">{post.metaDescription}</p>
                      <span className="flex items-center gap-1 text-xs text-[#C5A059] mt-auto">
                        Leggi articolo <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
